import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import FullscreenLoader from "../components/FullscreenLoader";
import axios from "axios";
import { FormDataType, UserType } from "../helpers/types";
import { looksLikeFileValue } from "../helpers/fileFields";
import { useToast } from "../components/ToastProvider";
import { useTranslation } from "react-i18next";

type AuthContextType = {
    accessToken: string;
    sub: string;
    user: UserType | null;
    login: (nin: string) => Promise<void>;
    logout: () => void;
    apiCall: (data: Record<string, any>) => Promise<any>;
    setUserData: (data: UserType) => void;
    saveUserProfile: (data: FormDataType) => any;
};

const AuthContext = createContext<AuthContextType | null>(null);

const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (context === null) {
        throw new Error('useAuthContext must be used within a AuthProvider');
    }

    return context;
}

const AuthProvider = ({ children }: { children: ReactNode }) => {
    const auth = useAuth();
    const { showToast } = useToast();
    const { t } = useTranslation();
    const [user, setUser] = useState<UserType | null>(null);
    const [accessToken, setAccessToken] = useState(auth?.user?.access_token || '');
    const [sub, setSub] = useState(auth?.user?.profile?.sub || '');
    const [loading, setLoading] = useState(true);
    const [earlyAccess, setEarlyAccess] = useState<boolean>(false);

    const getEarlyAccess = async () => {
        const value = localStorage.getItem('earlyAccess');
        if (value) {
            setEarlyAccess(value === 'true');
        }
        return value;
    }

    useEffect(() => {
        getEarlyAccess();
    }, []);

    const subscribeEarlyAccess = async (value: boolean) => {
        localStorage.setItem('earlyAccess', value.toString());
        setEarlyAccess(value);
    }

    const doApiCall = async (data: Record<string, any>, subID = sub) => {
        const formData = new FormData();
        formData.append("sub", subID)
        //formData.append("auth", token);

        const appendToFormData = (key: string, value: any) => {
            // Filfelter: send BARE nye File-objekter. Allerede lagrede filer
            // beskrives av `existing_attachments`, og en beskrivelse som
            // serialiseres her blir til `felt[0][name]` i $_POST — som API-et
            // lagrer i søknadens payload, der den gjenoppliver filer søkeren
            // har fjernet.
            //
            // Testen er på VERDIEN, ikke på feltnavnet. `filer_*` var en
            // navnekonvensjon som ikke holder: feltnavn er fritekst stiftelsen
            // skriver i lyfti-form-builder, og default-navnet er `newFileupload1`.
            //
            // Gjelder KUN toppnivå-felt. Et bilag i en utleggsrad
            // (`tabell[0][bilag]`) er en referanse `{name, location}` som MÅ
            // sendes: API-et beholder bare bilagene en rad peker på, så en
            // referanse som falt bort her slettet bilaget ved neste lagring.
            const isTopLevel = !key.includes('[');
            if (isTopLevel && (key.startsWith('filer_') || looksLikeFileValue(value))) {
                if (value instanceof File) {
                    formData.append(key + '[]', value);
                } else if (Array.isArray(value)) {
                    value.forEach((file) => {
                        if (file instanceof File) {
                            formData.append(key + '[]', file);
                        }
                    })
                }
            } else if (value instanceof File) {
                formData.append(key + '[]', value);
            } else if (Array.isArray(value)) {
            value.forEach((item, index) => {
                if (typeof item === 'object' && item !== null && !(item instanceof File)) {
                    appendToFormData(`${key}[${index}]`, item);  // recurse into row objects
                } else {
                    formData.append(`${key}[]`, item);  // primitives and Files stay flat
                }
            });
            } else if (typeof value === 'object' && value !== null) {
                for (const subKey in value) {
                    appendToFormData(`${key}[${subKey}]`, value[subKey]);
                }
            } else if (value !== undefined && value !== null) {
                formData.append(key, value);
            }
        }

        for (const key in data) {
            appendToFormData(key, data[key]);
        }
        const saved = localStorage.getItem('lyfti_selected_entity');
        formData.append('hostname', saved || window.location.hostname);

        try {
            const status = await axios({
                method: 'post',
                url: import.meta.env.VITE_API_URL,
                data: formData,
                headers: { "Content-Type": "multipart/form-data" }
            });

            if (status.data.status === 401) {
                auth.signoutRedirect({ id_token_hint: auth?.user?.id_token, post_logout_redirect_uri: window.location.origin });
                auth.removeUser();
            }

            // Need to check for valid accesstoken
            return status;
        } catch (error) {
            console.error("doApiCall ERROR: ", error);
            showToast(t('error_generic_api'), 'danger');
        }
    }

    const getUserData = async () => {
        const response = await doApiCall({
            action: 'getUserData',
        }, auth?.user?.profile?.sub);

        if (response?.data?.status === 200) {
            return response.data.data;
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            if (auth?.isLoading) {
                setLoading(true);
            } else if (auth?.user) {
                // Setting accesstoken from OIDC
                setAccessToken(auth?.user?.access_token);
                const newSub = auth?.user?.profile?.sub ?? '';
                setSub(newSub);

                // Signaler til ConfigProvider (som mounter utenfor OIDC-konteksten)
                // at vi nå har en gyldig `sub` slik at maler kan hentes fra
                // /rest/applicationtemplate. Custom event slipper vi en direkte
                // avhengighet mellom de to providerne.
                if (newSub) {
                    window.dispatchEvent(
                        new CustomEvent('lyfti:sub-ready', { detail: { sub: newSub } })
                    );
                }

                // Getting user data from database
                const data = await getUserData();
                setUser(data);

                setLoading(false);
            } else {
                setLoading(false);
            }
        };

        fetchData();
    }, [auth]);

    // If BANKID is not working use this for development
    /* useEffect(() => {
        setAccessToken("dummytoken");
        setSub("04BpoJ21IjxK-4dKp8r5Rny6DxgKmt1UAI9Q-LZAxH0=");
        setUser({
            id: 1571,
            username: 'henrik@catchmedian.no',
            fullname: 'Gustavo Silva',
            email: 'henrik@catchmedian.no',
            phone: '97764757',
            address: 'Benterudstranda 9',
            zip: '3511',
            city: 'Hønefiss',
            bankid: 'test',
            verified_email: true
        })
    }, []); */

    return (
        <AuthContext.Provider value={{
            accessToken,
            sub,
            user,
            // Functions
            login: async (nin) => {
                const value = nin.replace(/\s+/g, "");
                // Log login attempt
                await doApiCall({
                    action: 'logLogin',
                });

                // Proceed with OIDC login. Siden søkeren står på blir med som
                // state, så onSigninCallback i App.tsx sender hen tilbake dit
                // (f.eks. en lenke til /soknad/8291) i stedet for til Din side.
                await auth.signinRedirect({
                    state: { returnTo: window.location.pathname + window.location.search },
                    extraQueryParams: {
                        login_hint: `nin:${value}`
                    }
                });
            },
            logout: () => {
                auth.signoutRedirect({ id_token_hint: auth?.user?.id_token, post_logout_redirect_uri: window.location.origin });
                auth.removeUser();
            },
            apiCall: async (data: Record<string, any>) => {
                const status = await doApiCall(data)
                return status
            },
            setUserData: (data: UserType) => {
                setUser(data);
            },
            saveUserProfile: async (data: FormDataType) => {
                const status = await doApiCall({
                    action: 'saveUserProfile',
                    data
                });
                return status;
            },
            subscribeEarlyAccess,
            earlyAccess,
        }}>
            {!loading ? children : <FullscreenLoader />}
        </AuthContext.Provider>
    )
}

export { useAuthContext, AuthProvider };